export const COLLABORATION_PROTOCOL = 'MCRO-COLLAB/1';
export const COLLABORATION_SCHEMA = 'choreo-collaborative-document/1';
export const MAX_SOURCE_BYTES = 1024 * 1024;
export const MAX_LINES = 5000;
export const MAX_INSERT_BYTES = 32 * 1024;
export const MAX_ENVELOPE_BYTES = 64 * 1024;

const encoder = new TextEncoder();
export const utf8Bytes = (value) => encoder.encode(String(value)).byteLength;
const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
export const isScalarBoundary = (text, offset) => {
  if (!Number.isSafeInteger(offset) || offset < 0 || offset > text.length) return false;
  if (offset === 0 || offset === text.length) return true;
  const before = text.charCodeAt(offset - 1); const after = text.charCodeAt(offset);
  return !(before >= 0xd800 && before <= 0xdbff && after >= 0xdc00 && after <= 0xdfff);
};
const requireBoundary = (text, offset) => { if (!isScalarBoundary(text, offset)) throw new Error('An edit cannot split a Unicode character.'); };
const operationTooLarge = (message) => { const error = new Error(message); error.code = 'OPERATION_TOO_LARGE'; throw error; };

export function uuidV7(now = Date.now(), cryptoObject = globalThis.crypto) {
  if (!cryptoObject?.getRandomValues) {
    const error = new Error('Secure randomness is required for collaborative identities.'); error.code = 'SERVICE_UNAVAILABLE'; throw error;
  }
  const bytes = new Uint8Array(16);
  cryptoObject.getRandomValues(bytes);
  let timestamp = BigInt(now);
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = Number(timestamp & 0xffn);
    timestamp >>= 8n;
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getClientInstanceId(userId) {
  const key = `studioaeye_choreo_client_v1:${String(userId || 'anonymous')}`;
  try {
    const existing = window.localStorage.getItem(key);
    if (UUID_V7.test(existing || '')) return existing;
    const created = uuidV7();
    window.localStorage.setItem(key, created);
    return created;
  } catch {
    return uuidV7();
  }
}

export const orderedLines = (document) => [...(document?.lines || [])]
  .sort((left, right) => String(left.orderKey).localeCompare(String(right.orderKey)) || String(left.sourceLineId).localeCompare(String(right.sourceLineId)));

export const exactSource = (document) => orderedLines(document).map((line) => `${line.text}${line.eol}`).join('');

export const containsServerOnlyCompound = (operation) => operation?.type === 'transaction.compound'
  || (Array.isArray(operation?.operations) && operation.operations.some(containsServerOnlyCompound));

export function validateBootstrap(value, organisationId, routineId) {
  if (value?.protocolVersion !== COLLABORATION_PROTOCOL || value?.document?.schema !== COLLABORATION_SCHEMA) {
    const error = new Error('This browser cannot edit this collaboration protocol. Update the application to continue.');
    error.code = 'UNSUPPORTED_PROTOCOL';
    throw error;
  }
  if (String(value.organisationId) !== String(organisationId) || String(value.routineId) !== String(routineId)
      || String(value.document.organisationId) !== String(organisationId) || value.document.routineId !== routineId
      || value.document.draftId !== value.draftId || !UUID_V7.test(value.draftId) || !Number.isSafeInteger(Number(value.serverSequence))) {
    const error = new Error('The collaborative draft identity could not be verified.');
    error.code = 'INVALID_ENVELOPE';
    throw error;
  }
  const ids = new Set();
  const lines = orderedLines(value.document);
  if (lines.length > MAX_LINES || utf8Bytes(exactSource(value.document)) > MAX_SOURCE_BYTES) {
    const error = new Error('The collaborative document exceeds the supported limits.');
    error.code = 'DOCUMENT_TOO_LARGE';
    throw error;
  }
  lines.forEach((line) => {
    if (!UUID_V7.test(line?.sourceLineId || '') || ids.has(line.sourceLineId) || !line.orderKey || ids.has(`order:${line.orderKey}`)
      || typeof line.text !== 'string' || !['', '\n', '\r', '\r\n'].includes(line.eol)) {
      const error = new Error('The collaborative document contains invalid line identities.');
      error.code = 'INVALID_UPDATE';
      throw error;
    }
    ids.add(line.sourceLineId);
    ids.add(`order:${line.orderKey}`);
  });
  return { ...value.document, serverSequence: Number(value.serverSequence) };
}

const reindex = (lines) => lines.map((line, index) => ({ ...line, orderKey: String(index + 1).padStart(12, '0') }));

export function applyOperation(input, operation, serverSequence = Number(input?.serverSequence || 0), actorId = 'system', inverseContext = false) {
  let document = JSON.parse(JSON.stringify(input));
  if (!operation || typeof operation !== 'object' || typeof operation.type !== 'string') throw new Error('Collaborative operation is invalid.');
  const hasNestedTransaction = (operations) => operations.some((nested) => String(nested?.type || '').startsWith('transaction.'));
  if (operation.type === 'transaction.compound' && (!operation.originatingOperationId || !Array.isArray(operation.operations) || !operation.operations.length
    || hasNestedTransaction(operation.operations))) throw new Error('Compound transaction is invalid.');
  if (operation.type === 'transaction.inverse' && (!Array.isArray(operation.ofOperationIds) || operation.ofOperationIds.length !== 1 || !Array.isArray(operation.operations) || !operation.operations.length
    || hasNestedTransaction(operation.operations))) throw new Error('Inverse transaction is invalid.');
  if (operation.type === 'transaction.edit' && (!Array.isArray(operation.operations) || !operation.operations.length
    || hasNestedTransaction(operation.operations))) throw new Error('Edit transaction is invalid.');
  if (operation.type === 'transaction.edit' || operation.type === 'transaction.compound' || operation.type === 'transaction.inverse') {
    operation.operations.forEach((nested) => { document = applyOperation(document, nested, serverSequence, actorId, inverseContext || operation.type === 'transaction.inverse'); });
    document.serverSequence = serverSequence;
    return document;
  }
  const lines = orderedLines(document);
  const indexOf = (lineId) => lines.findIndex((line) => line.sourceLineId === lineId);
  if (operation.type === 'text.insert') {
    const index = indexOf(operation.lineId); if (index < 0) throw new Error('Line is no longer available.');
    if (typeof operation.text !== 'string') throw new Error('Inserted text is invalid.');
    if (utf8Bytes(operation.text) > MAX_INSERT_BYTES) operationTooLarge('One edit cannot insert more than 32 KiB.');
    requireBoundary(lines[index].text, operation.offset);
    lines[index].text = `${lines[index].text.slice(0, operation.offset)}${operation.text}${lines[index].text.slice(operation.offset)}`;
  } else if (operation.type === 'text.delete') {
    const index = indexOf(operation.lineId); if (index < 0) throw new Error('Line is no longer available.');
    if (operation.to < operation.from) throw new Error('Delete range is invalid.');
    requireBoundary(lines[index].text, operation.from); requireBoundary(lines[index].text, operation.to);
    lines[index].text = `${lines[index].text.slice(0, operation.from)}${lines[index].text.slice(operation.to)}`;
  } else if (operation.type === 'line.split') {
    const index = indexOf(operation.lineId); if (index < 0) throw new Error('Line is no longer available.');
    if (lines.length >= MAX_LINES || !['\n', '\r', '\r\n'].includes(operation.eol)) throw new Error('Line split exceeds document limits or has an invalid line ending.');
    const tombstone = document.tombstones?.[operation.newLineId];
    if (!UUID_V7.test(operation.newLineId || '') || lines.some((line) => line.sourceLineId === operation.newLineId)
      || (tombstone && !inverseContext)) throw new Error('New line identity is invalid or already used.');
    requireBoundary(lines[index].text, operation.offset);
    const line = lines[index];
    lines.splice(index, 1, { ...line, text: line.text.slice(0, operation.offset), eol: operation.eol }, {
      sourceLineId: operation.newLineId, text: line.text.slice(operation.offset), eol: line.eol, orderKey: '',
    });
    if (inverseContext && tombstone) { document.tombstones = { ...(document.tombstones || {}) }; delete document.tombstones[operation.newLineId]; }
  } else if (operation.type === 'line.join') {
    const index = indexOf(operation.leftLineId);
    if (index < 0 || lines[index + 1]?.sourceLineId !== operation.rightLineId) throw new Error('Lines are no longer adjacent.');
    const right = lines[index + 1]; const offset = lines[index].text.length;
    lines[index] = { ...lines[index], text: lines[index].text + right.text, eol: right.eol };
    lines.splice(index + 1, 1);
    document.tombstones = { ...(document.tombstones || {}), [right.sourceLineId]: {
      deletedSourceLineId: right.sourceLineId, deletedAtSequence: serverSequence,
      redirect: { sourceLineId: lines[index].sourceLineId, field: 'source', offset },
    } };
  } else if (operation.type === 'line.delete') {
    const index = indexOf(operation.lineId); if (index < 0) throw new Error('Line is no longer available.');
    const [removed] = lines.splice(index, 1);
    const target = lines[index] ? { sourceLineId: lines[index].sourceLineId, field: 'source', offset: 0 }
      : lines[index - 1] ? { sourceLineId: lines[index - 1].sourceLineId, field: 'source', offset: lines[index - 1].text.length }
        : { documentStart: true };
    document.tombstones = { ...(document.tombstones || {}), [removed.sourceLineId]: {
      deletedSourceLineId: removed.sourceLineId, deletedAtSequence: serverSequence, redirect: target,
    } };
  } else if (operation.type === 'line.move') {
    const index = indexOf(operation.lineId); if (index < 0) throw new Error('Line is no longer available.');
    const [moving] = lines.splice(index, 1);
    const afterIndex = operation.afterLineId === null ? -1 : lines.findIndex((line) => line.sourceLineId === operation.afterLineId);
    if (operation.afterLineId !== null && afterIndex < 0) throw new Error('Move anchor is no longer available.');
    lines.splice(afterIndex + 1, 0, moving);
  } else if (operation.type === 'entity.set') {
    const current = document.reviewedOverrides?.[operation.entityId];
    if (!operation.entityId || !Number.isSafeInteger(operation.expectedEntityVersion) || operation.expectedEntityVersion < 0
      || Number(current?.entityVersion || 0) !== operation.expectedEntityVersion) throw new Error('Entity version changed.');
    document.reviewedOverrides = { ...(document.reviewedOverrides || {}), [operation.entityId]: {
      ...(current || {}), entityId: operation.entityId, entityVersion: operation.expectedEntityVersion + 1,
      value: operation.value, reason: operation.reason, actorId: String(actorId),
    } };
  } else throw new Error('Unsupported collaborative operation.');
  document.lines = reindex(lines);
  document.serverSequence = serverSequence;
  if (document.lines.length > MAX_LINES || utf8Bytes(exactSource(document)) > MAX_SOURCE_BYTES) operationTooLarge('The collaborative document exceeds the supported limits.');
  return document;
}

// Builds the exact server-verifiable inverse against the accepted operation's
// immediate pre-state. Scalar writes and structurally ambiguous deletes return
// null because MCRO-COLLAB/1 cannot safely reconstruct them.
export function inverseOperations(documentBefore, operation, acceptedContext = false) {
  if (!operation || typeof operation !== 'object') return null;
  if (['transaction.edit', 'transaction.compound', 'transaction.inverse'].includes(operation.type)) {
    if (!Array.isArray(operation.operations) || !operation.operations.length) return null;
    let document = documentBefore; let inverse = [];
    for (const nested of operation.operations) {
      const nestedInverse = inverseOperations(document, nested, acceptedContext); if (!nestedInverse) return null;
      document = applyOperation(document, nested, document.serverSequence, 'system', acceptedContext || operation.type === 'transaction.inverse');
      inverse = [...nestedInverse, ...inverse];
    }
    return inverse;
  }
  const lines = orderedLines(documentBefore); const line = (lineId) => lines.find((item) => item.sourceLineId === lineId);
  if (operation.type === 'text.insert') return [{ type: 'text.delete', lineId: operation.lineId,
    from: operation.offset, to: operation.offset + operation.text.length }];
  if (operation.type === 'text.delete') {
    const target = line(operation.lineId); if (!target) return null;
    return [{ type: 'text.insert', lineId: operation.lineId, offset: operation.from, text: target.text.slice(operation.from, operation.to) }];
  }
  if (operation.type === 'line.split') return [{ type: 'line.join', leftLineId: operation.lineId, rightLineId: operation.newLineId }];
  if (operation.type === 'line.join') {
    const left = line(operation.leftLineId); const right = line(operation.rightLineId);
    if (!left || !right || lines.indexOf(right) !== lines.indexOf(left) + 1) return null;
    return [{ type: 'line.split', lineId: operation.leftLineId, offset: left.text.length, newLineId: operation.rightLineId, eol: left.eol }];
  }
  if (operation.type === 'line.move') {
    const index = lines.findIndex((item) => item.sourceLineId === operation.lineId); if (index < 0) return null;
    return [{ type: 'line.move', lineId: operation.lineId, afterLineId: index > 0 ? lines[index - 1].sourceLineId : null, orderKey: lines[index].orderKey }];
  }
  if (operation.type === 'line.delete') {
    const targetIndex = lines.findIndex((item) => item.sourceLineId === operation.lineId);
    if (targetIndex < 0) return null;
    const target = lines[targetIndex]; const previous = lines[targetIndex - 1]; const next = lines[targetIndex + 1];
    const splitEol = (value) => (['\n', '\r\n', '\r'].includes(value) ? value : null);
    let inverse;
    if (previous && next && splitEol(previous.eol) && splitEol(target.eol)) {
      inverse = [
        { type: 'line.split', lineId: previous.sourceLineId, offset: previous.text.length,
          newLineId: target.sourceLineId, eol: previous.eol },
        ...(target.text ? [{ type: 'text.insert', lineId: target.sourceLineId, offset: 0, text: target.text }] : []),
        { type: 'line.join', leftLineId: target.sourceLineId, rightLineId: next.sourceLineId },
        { type: 'line.split', lineId: target.sourceLineId, offset: target.text.length,
          newLineId: next.sourceLineId, eol: target.eol },
      ];
    } else if (!previous && next && splitEol(next.eol) && splitEol(target.eol)) {
      inverse = [
        { type: 'line.split', lineId: next.sourceLineId, offset: 0,
          newLineId: target.sourceLineId, eol: next.eol },
        { type: 'line.move', lineId: target.sourceLineId, afterLineId: null, orderKey: `undo:${target.sourceLineId}` },
        { type: 'line.join', leftLineId: target.sourceLineId, rightLineId: next.sourceLineId },
        ...(target.text ? [{ type: 'text.insert', lineId: target.sourceLineId, offset: 0, text: target.text }] : []),
        { type: 'line.split', lineId: target.sourceLineId, offset: target.text.length,
          newLineId: next.sourceLineId, eol: target.eol },
      ];
    } else if (previous && !next && previous.eol === target.eol && splitEol(previous.eol)) {
      inverse = [
        { type: 'line.split', lineId: previous.sourceLineId, offset: previous.text.length,
          newLineId: target.sourceLineId, eol: previous.eol },
        ...(target.text ? [{ type: 'text.insert', lineId: target.sourceLineId, offset: 0, text: target.text }] : []),
      ];
    } else return null;

    try {
      const changed = applyOperation(documentBefore, operation, Number(documentBefore.serverSequence || 0) + 1);
      const restored = applyOperation(changed, { type: 'transaction.inverse', ofOperationIds: ['inverse-verification'], operations: inverse },
        Number(documentBefore.serverSequence || 0) + 2);
      const expected = JSON.parse(JSON.stringify(documentBefore)); expected.serverSequence = restored.serverSequence;
      if (JSON.stringify(restored) !== JSON.stringify(expected)) return null;
    } catch { return null; }
    return inverse;
  }
  return null;
}

// Operations behind a local optimistic queue are authored after earlier local items.
// Only remote accepted text operations need to be transformed into their coordinates.
export function transformThroughRemote(operationInput, remote, originalOperationId) {
  const operation = JSON.parse(JSON.stringify(operationInput));
  if (['transaction.edit', 'transaction.inverse', 'transaction.compound'].includes(operation.type)) {
    const key = operation.originatingOperationId || originalOperationId;
    return { ...operation, operations: operation.operations.map((nested, index) => transformThroughRemote(nested, remote, `${key}:${index}`)) };
  }
  if (['transaction.edit', 'transaction.inverse', 'transaction.compound'].includes(remote.type)) {
    return remote.operations.reduce((next, nested) => transformThroughRemote(next, nested, originalOperationId), operation);
  }
  if (operation.type === 'text.insert' && remote.type === 'text.insert' && operation.lineId === remote.lineId && remote.offset <= operation.offset) operation.offset += remote.text.length;
  if (operation.type === 'text.insert' && remote.type === 'text.delete' && operation.lineId === remote.lineId) operation.offset = operation.offset <= remote.from ? operation.offset : operation.offset <= remote.to ? remote.from : operation.offset - (remote.to - remote.from);
  if (operation.type === 'text.delete' && remote.type === 'text.insert' && operation.lineId === remote.lineId
    && remote.offset > operation.from && remote.offset < operation.to) {
    if (!originalOperationId) throw new Error('Compound delete attribution is required.');
    return { type: 'transaction.compound', originatingOperationId: originalOperationId, operations: [
      { type: 'text.delete', lineId: operation.lineId, from: remote.offset + remote.text.length, to: operation.to + remote.text.length },
      { type: 'text.delete', lineId: operation.lineId, from: operation.from, to: remote.offset },
    ] };
  }
  if (operation.type === 'text.delete' && remote.type === 'text.insert' && operation.lineId === remote.lineId) {
    if (remote.offset <= operation.from) { operation.from += remote.text.length; operation.to += remote.text.length; }
    else if (remote.offset < operation.to) operation.to += remote.text.length;
  }
  if (operation.type === 'text.delete' && remote.type === 'text.delete' && operation.lineId === remote.lineId) {
    const beforeFrom = Math.max(0, Math.min(remote.to, operation.from) - Math.min(remote.from, operation.from));
    const beforeTo = Math.max(0, Math.min(remote.to, operation.to) - Math.min(remote.from, operation.to));
    operation.from -= beforeFrom; operation.to = Math.max(operation.from, operation.to - beforeTo);
  }
  return operation;
}

export function transformPendingThroughRemote(operationInput, remote, originalOperationId, documentBefore, remoteAcceptedContext = false) {
  if (['transaction.edit', 'transaction.inverse', 'transaction.compound'].includes(remote.type)) {
    let document = documentBefore; let operation = operationInput;
    remote.operations.forEach((nested) => {
      operation = transformPendingThroughRemote(operation, nested, originalOperationId, document, remoteAcceptedContext);
      document = applyOperation(document, nested, document.serverSequence, 'system', remoteAcceptedContext);
    });
    return operation;
  }
  const operation = JSON.parse(JSON.stringify(operationInput));
  if (['transaction.edit', 'transaction.inverse'].includes(operation.type)) return { ...operation,
    operations: operation.operations.map((nested, index) => transformPendingThroughRemote(nested, remote,
      `${originalOperationId}:${index}`, documentBefore, remoteAcceptedContext)) };
  const targets = [operation.lineId, operation.leftLineId, operation.rightLineId].filter(Boolean);
  if (remote.type === 'line.delete' && targets.includes(remote.lineId)) throw new Error('A line with local changes was deleted remotely.');
  if (remote.type === 'line.split') {
    if (operation.type === 'text.insert' && operation.lineId === remote.lineId && operation.offset > remote.offset) return { ...operation, lineId: remote.newLineId, offset: operation.offset - remote.offset };
    if (operation.type === 'text.delete' && operation.lineId === remote.lineId) {
      if (operation.from >= remote.offset) return { ...operation, lineId: remote.newLineId, from: operation.from - remote.offset, to: operation.to - remote.offset };
      if (operation.to > remote.offset) throw new Error('A remote split intersects local text changes.');
    }
    if (operation.type === 'line.split' && operation.lineId === remote.lineId) throw new Error('The same line was split remotely.');
  }
  if (remote.type === 'line.join') {
    const left = orderedLines(documentBefore).find((line) => line.sourceLineId === remote.leftLineId);
    if (operation.lineId === remote.rightLineId && ['text.insert', 'text.delete', 'line.split'].includes(operation.type)) {
      const offset = left?.text.length || 0; const next = { ...operation, lineId: remote.leftLineId };
      if (operation.type === 'text.delete') { next.from += offset; next.to += offset; } else next.offset += offset;
      return next;
    }
    if (targets.includes(remote.rightLineId) && operation.type !== 'line.move') throw new Error('A line with local structure was joined remotely.');
  }
  if (['line.move', 'line.split', 'line.join'].includes(remote.type)
    && ['line.move', 'line.split', 'line.join', 'line.delete'].includes(operation.type)
    && targets.some((target) => [remote.lineId, remote.leftLineId, remote.rightLineId].includes(target))) throw new Error('Concurrent structural edits need comparison.');
  return transformThroughRemote(operation, remote, originalOperationId);
}

// ——— Remote semantic cursors (MCRO001-D004 §5.3 / T-MCRO-024 B–C) ———

const transformOffsetThroughTextDelete = (offset, from, to) => {
  if (offset <= from) return offset;
  if (offset >= to) return offset - (to - from);
  return from;
};

// Exact client port of the server's transformCollaborationAnchorWithRedirects
// (studioaeye-choreo backend/src/services/choreographyDocument.ts). Deleted-line
// redirects come only from durable tombstones; a line that vanishes with no
// redirect removes the anchor (returns null) — the client never invents a
// replacement line identity.
export function transformCollaborationAnchorWithRedirects(input, operation, redirects) {
  let anchor = { ...input };
  let removed = false;
  const apply = (item) => {
    if (removed) return;
    if (item.type === 'transaction.edit' || item.type === 'transaction.compound' || item.type === 'transaction.inverse') {
      item.operations.forEach(apply);
      return;
    }
    if (item.type === 'text.insert' && anchor.sourceLineId === item.lineId) {
      if (anchor.offset >= item.offset) anchor.offset += item.text.length;
      if (anchor.selectionEnd !== undefined && anchor.selectionEnd >= item.offset) anchor.selectionEnd += item.text.length;
      return;
    }
    if (item.type === 'text.delete' && anchor.sourceLineId === item.lineId) {
      anchor.offset = transformOffsetThroughTextDelete(anchor.offset, item.from, item.to);
      if (anchor.selectionEnd !== undefined) {
        anchor.selectionEnd = transformOffsetThroughTextDelete(anchor.selectionEnd, item.from, item.to);
      }
      return;
    }
    if (item.type === 'line.split' && anchor.sourceLineId === item.lineId) {
      const offsetMoves = anchor.offset > item.offset;
      const selectionMoves = anchor.selectionEnd !== undefined && anchor.selectionEnd > item.offset;
      if (offsetMoves) {
        anchor.sourceLineId = item.newLineId;
        anchor.offset -= item.offset;
      }
      if (anchor.selectionEnd !== undefined) {
        if (selectionMoves === offsetMoves) {
          if (selectionMoves) anchor.selectionEnd -= item.offset;
        } else {
          // The wire anchor can represent a selection on only one source line.
          // Collapse a cross-line selection to its focus rather than publish an
          // invalid range bound to two identities.
          delete anchor.selectionEnd;
        }
      }
      return;
    }
    if (item.type === 'line.join' && anchor.sourceLineId === item.rightLineId) {
      const redirect = redirects[item.rightLineId];
      if (redirect && 'sourceLineId' in redirect) {
        anchor.sourceLineId = redirect.sourceLineId;
        anchor.offset += redirect.offset;
        if (anchor.selectionEnd !== undefined) anchor.selectionEnd += redirect.offset;
      }
      return;
    }
    if (item.type === 'line.delete' && anchor.sourceLineId === item.lineId) {
      const redirect = redirects[item.lineId];
      if (redirect && 'sourceLineId' in redirect) {
        anchor = { sourceLineId: redirect.sourceLineId, field: 'source', offset: redirect.offset };
      } else removed = true;
    }
  };
  apply(operation);
  return removed ? null : anchor;
}

export const collaborationAnchorRedirects = (document) => Object.fromEntries(Object.entries(document?.tombstones || {})
  .filter(([, tombstone]) => tombstone && typeof tombstone === 'object' && tombstone.redirect)
  .map(([deletedLineId, tombstone]) => [deletedLineId, tombstone.redirect]));

// Exact client port of the server's transformCollaborationAnchor: transform
// through one accepted operation, then clamp against the post-update document.
// Null means the anchored line is gone entirely and the cursor disappears.
export function transformCollaborationAnchor(input, operation, postUpdateDocument) {
  const transformed = transformCollaborationAnchorWithRedirects(input, operation, collaborationAnchorRedirects(postUpdateDocument));
  if (!transformed) return null;
  const line = (postUpdateDocument.lines || []).find((candidate) => candidate.sourceLineId === transformed.sourceLineId);
  if (!line) return null;
  transformed.offset = Math.min(transformed.offset, line.text.length);
  if (transformed.selectionEnd !== undefined) transformed.selectionEnd = Math.min(transformed.selectionEnd, line.text.length);
  return transformed;
}

// Places remote canonical-coordinate anchors onto the visible optimistic
// document: each anchor is folded through the local pending (unacknowledged)
// queue exactly as the visible document itself is derived, using the visible
// document's accumulated tombstone redirects. Anchors whose line is absent from
// the visible document are dropped.
export function projectRemoteCursors(document, cursors = {}, pendingOperations = []) {
  if (!document) return [];
  const lines = orderedLines(document);
  const redirects = collaborationAnchorRedirects(document);
  const placements = [];
  for (const [clientInstanceId, cursor] of Object.entries(cursors)) {
    let anchor = cursor?.anchor;
    if (!anchor || anchor.field !== 'source') continue;
    try {
      for (const operation of pendingOperations) {
        if (!anchor) break;
        anchor = transformCollaborationAnchorWithRedirects(anchor, operation, redirects);
      }
    } catch { anchor = null; }
    if (!anchor) continue;
    const index = lines.findIndex((line) => line.sourceLineId === anchor.sourceLineId);
    if (index < 0) continue;
    const text = lines[index].text;
    const offset = Math.max(0, Math.min(text.length, Number(anchor.offset) || 0));
    const selectionEnd = Number.isSafeInteger(anchor.selectionEnd)
      ? Math.max(0, Math.min(text.length, anchor.selectionEnd)) : undefined;
    placements.push({ clientInstanceId, sourceLine: index + 1, offset,
      ...(selectionEnd !== undefined && selectionEnd !== offset ? { selectionEnd } : {}),
      actor: cursor.actor || {}, receivedAt: cursor.receivedAt });
  }
  return placements;
}

// Deterministic per-person presentation colour (D004 §5.3): one stable palette
// member per user identity, used identically by the presence strip and every
// remote caret. The palette avoids red/green-only pairs and keeps ≥4.5:1
// contrast for white label text and for a caret bar on the cream canvas.
export const COLLABORATOR_COLOR_PALETTE = ['#1d4ed8', '#b45309', '#0f766e', '#7c3aed', '#be123c', '#0369a1', '#a21caf', '#475569'];

export function collaboratorColor(userId) {
  let hash = 2166136261;
  for (const character of String(userId ?? '')) {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return COLLABORATOR_COLOR_PALETTE[hash % COLLABORATOR_COLOR_PALETTE.length];
}

export function collaboratorSelectionColor(userId) {
  const hex = collaboratorColor(userId);
  return `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, 0.22)`;
}

// MCRO001-F0065 (T-MCRO-065): deterministic per-GROUP presentation colour
// from the SAME stable palette and the SAME FNV-1a hash convention as
// collaboratorColor above, keyed by the @group name. Group variations are
// colour-coded on the canvas (D015 §2: the staging PDFs are colour-coded
// groups); a given group name wears the same colour everywhere it appears.
export function groupColor(name) {
  return collaboratorColor(`group:${String(name ?? '')}`);
}

export function groupSelectionColor(name) {
  const hex = groupColor(name);
  return `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}, 0.14)`;
}

export function canvasScore(document) {
  const sections = []; const blocks = []; let count = 1; let currentSection = null; let currentSequence = '';
  orderedLines(document).forEach((line, lineIndex) => {
    const section = line.text.match(/^#\s+(.+)$/); const sequence = line.text.match(/^##\s+(.+)$/);
    if (section && !line.text.startsWith('##')) {
      if (currentSection) currentSection.endCount = Math.max(currentSection.startCount, count - 1);
      currentSection = { id: `section:${line.sourceLineId}`, label: section[1].trim(), startCount: count, endCount: count,
        sourceLineId: line.sourceLineId, lineNumber: lineIndex + 1, blocks: [] };
      sections.push(currentSection); currentSequence = ''; return;
    }
    if (sequence) { currentSequence = sequence[1].trim(); return; }
    const allocation = line.text.match(/^\[(?:(\d+)\s*[x×]\s*8|(\d+))(?:b|c)?\]\s*(.*)$/i); if (!allocation) return;
    const length = allocation[1] ? Number(allocation[1]) * 8 : Number(allocation[2]); if (!Number.isSafeInteger(length) || length < 1 || length > 1024) return;
    const block = { id: `allocation:${line.sourceLineId}`, sourceLineId: line.sourceLineId, lineNumber: lineIndex + 1,
      startCount: count, countLength: length, label: allocation[3].trim() || currentSequence || `Counts ${count}–${count + length - 1}`,
      sequenceTitle: currentSequence, certainty: 'explicit' };
    blocks.push(block); if (currentSection) currentSection.blocks.push(block); count += length;
  });
  if (currentSection) currentSection.endCount = Math.max(currentSection.startCount, count - 1);
  return { sections, blocks, totalCounts: Math.max(0, count - 1) };
}

export async function verifyDocumentHash(document, expectedHash, cryptoObject = globalThis.crypto) {
  if (!/^[0-9a-f]{64}$/.test(String(expectedHash || ''))) return false;
  if (!cryptoObject?.subtle?.digest) {
    // T-MCRO-066 / MCRO001-F0066 (D004 §4.1.3): absent SubtleCrypto means this page
    // is not a secure context (plain HTTP) — an environment fact, not a hash
    // mismatch. Classify it honestly instead of the false INVALID_ENVELOPE conflict;
    // the gate stays fail-closed: this can never pass verification.
    const error = new Error('A secure connection is required to verify the shared choreography before editing.');
    error.code = 'SECURE_CONTEXT_REQUIRED';
    throw error;
  }
  const digest = await cryptoObject.subtle.digest('SHA-256', encoder.encode(exactSource(document)));
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('') === expectedHash;
}

export function lineEditOperations(lineId, previous, next) {
  if (previous === next) return [];
  let prefix = 0;
  while (prefix < previous.length && prefix < next.length && previous[prefix] === next[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < previous.length - prefix && suffix < next.length - prefix
    && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix += 1;
  const removedTo = previous.length - suffix;
  requireBoundary(previous, prefix); requireBoundary(previous, removedTo); requireBoundary(next, prefix); requireBoundary(next, next.length - suffix);
  const inserted = next.slice(prefix, next.length - suffix);
  const operations = [];
  if (removedTo > prefix) operations.push({ type: 'text.delete', lineId, from: prefix, to: removedTo });
  if (inserted) {
    if (utf8Bytes(inserted) > MAX_INSERT_BYTES) {
      const error = new Error('One edit cannot insert more than 32 KiB.'); error.code = 'OPERATION_TOO_LARGE'; throw error;
    }
    operations.push({ type: 'text.insert', lineId, offset: prefix, text: inserted });
  }
  return operations;
}

// The continuous Canvas surface displays orderedLines joined by single '\n'
// separators (browsers normalise CR/CRLF inside a textarea); protocol offsets
// stay per-line so the display eol never leaks into text operations.
export const displaySource = (document) => orderedLines(document).map((line) => line.text).join('\n');

// Translates one contiguous textarea gesture (typing, Enter, Backspace across a
// newline, paste, cut) on the continuous Canvas into the discrete MCRO-COLLAB/1
// operations for the affected sourceLineIds. This is a positional translation of
// the single edited region — never a projected-snapshot diff.
export function sourceEditOperations(document, previous, next, makeLineId = uuidV7) {
  if (previous === next) return [];
  const lines = orderedLines(document);
  if (!lines.length) throw Object.assign(new Error('The collaborative document has no editable lines.'), { code: 'INVALID_UPDATE' });
  let prefix = 0;
  while (prefix < previous.length && prefix < next.length && previous[prefix] === next[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < previous.length - prefix && suffix < next.length - prefix
    && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]) suffix += 1;
  while (prefix > 0 && (!isScalarBoundary(previous, prefix) || !isScalarBoundary(next, prefix))) prefix -= 1;
  while (suffix > 0 && (!isScalarBoundary(previous, previous.length - suffix) || !isScalarBoundary(next, next.length - suffix))) suffix -= 1;
  const endPosition = previous.length - suffix;
  const starts = []; let cursor = 0;
  lines.forEach((line) => { starts.push(cursor); cursor += line.text.length + 1; });
  const lineAt = (position) => {
    for (let index = 0; index < lines.length; index += 1) {
      if (position <= starts[index] + lines[index].text.length) return index;
    }
    return lines.length - 1;
  };
  const start = lineAt(prefix); const end = Math.max(start, lineAt(endPosition));
  const startOffset = prefix - starts[start];
  const operations = [];
  for (let index = start + 1; index <= end; index += 1) {
    operations.push({ type: 'line.join', leftLineId: lines[start].sourceLineId, rightLineId: lines[index].sourceLineId });
  }
  const removedLength = (endPosition - prefix) - (end - start);
  if (removedLength > 0) operations.push({ type: 'text.delete', lineId: lines[start].sourceLineId, from: startOffset, to: startOffset + removedLength });
  const parts = next.slice(prefix, next.length - suffix).split('\n');
  parts.forEach((part) => {
    if (utf8Bytes(part) > MAX_INSERT_BYTES) operationTooLarge('One edit cannot insert more than 32 KiB.');
  });
  if (parts[0]) operations.push({ type: 'text.insert', lineId: lines[start].sourceLineId, offset: startOffset, text: parts[0] });
  const eol = ['\n', '\r', '\r\n'].includes(lines[start].eol) ? lines[start].eol : '\n';
  let anchorId = lines[start].sourceLineId; let anchorOffset = startOffset + parts[0].length;
  for (let index = 1; index < parts.length; index += 1) {
    const newLineId = makeLineId();
    operations.push({ type: 'line.split', lineId: anchorId, offset: anchorOffset, newLineId, eol });
    if (parts[index]) operations.push({ type: 'text.insert', lineId: newLineId, offset: 0, text: parts[index] });
    anchorId = newLineId; anchorOffset = parts[index].length;
  }
  return operations;
}

export function makeEnvelope(resource, queued, baseServerSequence) {
  if (containsServerOnlyCompound(queued.operation)) {
    const error = new Error('A transformed compound operation must be derived and emitted by the server. Compare or export the local change.');
    error.code = 'INVALID_UPDATE'; throw error;
  }
  if (queued.redoOfOperationId && (typeof queued.redoOfOperationId !== 'string'
    || ['transaction.inverse', 'transaction.compound'].includes(queued.operation?.type))) {
    const error = new Error('Redo requires normal forward content with accepted Undo provenance.'); error.code = 'INVALID_UPDATE'; throw error;
  }
  const envelope = {
    protocolVersion: COLLABORATION_PROTOCOL,
    organisationId: String(resource.organisationId), routineId: resource.routineId, draftId: resource.draftId,
    clientInstanceId: resource.clientInstanceId, operationId: queued.operationId,
    clientSequence: queued.clientSequence, baseServerSequence, payload: queued.operation,
    ...(queued.redoOfOperationId ? { redoOfOperationId: queued.redoOfOperationId } : {}),
  };
  if (utf8Bytes(JSON.stringify(envelope)) > MAX_ENVELOPE_BYTES) {
    const error = new Error('One collaborative update cannot exceed 64 KiB.'); error.code = 'OPERATION_TOO_LARGE'; throw error;
  }
  return envelope;
}
